// ui/services/trueTodoKpiSummaryService.ts
// ✅ 覆蓋全文
//
// Purpose
// - Optional: read/subscribe server-side KPI summary document (dashboard_todos_kpi_summary)
// - Provide a "trigger rebuild" helper for admin/debug pages
//
// IMPORTANT
// - 此檔不應造成 TS 爆紅：Firebase 未設定 / 沒有 onSnapshot 時要能 graceful fail。

import * as fb from "../../firebaseClient";
import type { DailySummaryResult } from "./dailySummaryService";
import { offlineDemoPreset } from "./todosSummary";

// firebaseClient 在不同分支 export 內容可能不同。
// 這裡用 any 解耦：避免某分支沒 export onSnapshot / Firestore types 時整包炸掉。
const { db, doc, getDoc } = fb as any;

export type TrueTodoKpiSummary = DailySummaryResult & {
  updatedAt?: string;
  schemaVersion?: string;
};

async function tryReadDoc(path: { col: string; id: string }): Promise<any | null> {
  try {
    const ref = doc(db as any, path.col, path.id);
    const snap = await getDoc(ref as any);
    if (!snap?.exists?.()) return null;
    return snap.data?.() ?? null;
  } catch {
    return null;
  }
}

export async function getTrueTodoKpiSummaryOnce(): Promise<TrueTodoKpiSummary | null> {
  // 常見命名：current / latest（兩者都試）
  const current = await tryReadDoc({ col: "dashboard_todos_kpi_summary", id: "current" });
  if (current) return current as TrueTodoKpiSummary;

  const latest = await tryReadDoc({ col: "dashboard_todos_kpi_summary", id: "latest" });
  if (latest) return latest as TrueTodoKpiSummary;

  // ✅ Demo/離線展示保底：避免整個儀錶板三卡永遠 0（展示時比全空白好）
  try {
    const demo = offlineDemoPreset();
    return {
      ...(demo as any),
      updatedAt: new Date().toISOString(),
      schemaVersion: "offline_demo_preset",
    } as TrueTodoKpiSummary;
  } catch {
    return null;
  }
}

// --- Live subscribe (for DashboardTodosLiveDebugTab.tsx) ---
// NOTE: 某些分支 firebaseClient 可能沒有 export onSnapshot。
// 1) 若有 onSnapshot → 即時監聽 current
// 2) 若沒有 → 用 15 秒輪詢（demo/離線展示用也夠）
export function subscribeTrueTodoKpiSummary(
  onData: (v: TrueTodoKpiSummary | null) => void,
  onErr?: (e: any) => void
): () => void {
  // SSR / build-time safety
  // eslint-disable-next-line no-undef
  if (typeof window === "undefined") {
    onData(null);
    return () => void 0;
  }

  const onSnapshotFn = (fb as any).onSnapshot;

  // ✅ prefer real-time listener
  if (typeof onSnapshotFn === "function") {
    try {
      const ref = doc(db as any, "dashboard_todos_kpi_summary", "current");
      const unsub = onSnapshotFn(
        ref,
        (snap: any) => {
          try {
            if (snap?.exists?.()) onData((snap.data?.() ?? null) as TrueTodoKpiSummary);
            else {
              // current doc 不存在也給 demo preset（避免 0）
              void getTrueTodoKpiSummaryOnce().then(onData).catch((e) => onErr?.(e));
            }
          } catch (e: any) {
            onErr?.(e);
            void getTrueTodoKpiSummaryOnce().then(onData).catch((ee) => onErr?.(ee));
          }
        },
        (e: any) => {
          onErr?.(e);
          void getTrueTodoKpiSummaryOnce().then(onData).catch((ee) => onErr?.(ee));
        }
      );
      return typeof unsub === "function" ? unsub : () => void 0;
    } catch (e: any) {
      onErr?.(e);
      void getTrueTodoKpiSummaryOnce().then(onData).catch((ee) => onErr?.(ee));
      return () => void 0;
    }
  }

  // ✅ fallback polling
  let alive = true;
  const tick = async () => {
    if (!alive) return;
    try {
      const v = await getTrueTodoKpiSummaryOnce();
      if (!alive) return;
      onData(v);
    } catch (e: any) {
      onErr?.(e);
    }
  };

  void tick();
  const t = setInterval(() => void tick(), 15_000);

  return () => {
    alive = false;
    clearInterval(t);
  };
}

export async function triggerRebuildTrueTodoKpiSummaryNow(): Promise<{ ok: boolean; message?: string }> {
  // 若你有後端 endpoint，可在這裡接起來（此處 best-effort，不保證存在）
  try {
    // eslint-disable-next-line no-undef
    if (typeof window === "undefined") return { ok: false, message: "not_in_browser" };

    const endpoints = [
      "/api/admin/rebuildTrueTodoKpiSummary",
      "/api/rebuildTrueTodoKpiSummary",
      "/rebuildTrueTodoKpiSummary",
    ];

    let lastErr = "";

    for (const url of endpoints) {
      try {
        const resp = await fetch(url, { method: "POST" });
        if (resp.ok) return { ok: true, message: `POST ${url} ok` };
        lastErr = `${url} -> ${resp.status}`;
      } catch (e: any) {
        lastErr = `${url} -> ${String(e?.message ?? e)}`;
      }
    }

    return { ok: false, message: lastErr || "no_endpoint" };
  } catch (e: any) {
    return { ok: false, message: String(e?.message ?? e) };
  }
}
